import { GetStaticProps } from 'next';
import { NextSeo } from 'next-seo';
import dynamic from 'next/dynamic';
import React from 'react';

import { ContactUsMode } from '../components/forms/ContactForm';
import HeaderSection, { HeaderSectionType } from '../components/layout/sections/HeaderSection';
import PageSection, { PageSectionType } from '../components/layout/sections/PageSection';
import { StrapiApiClient } from '../lib/integrations/strapi/StrapiApiClient';
import { StrapiApiArticle, StrapiApiScrollingLogo } from '../lib/integrations/strapi/types';
import { withGlobalDefaultStaticProps } from '../lib/next-hooks/withGlobalDefaultStaticProps';
import { getPageSeoConfig } from '../lib/seo/pageSeoConfigs';
import pageRoutes from '../router/pageRoutes';
import { NextPageWithLayout } from '../types/global-types';
import ScrollingLogosWidget from '../widgets/animated/ScrollingLogosWidget';
import PricingCards from '../widgets/cards/PricingCards';
import CalculateROIWidget from '../widgets/interactive/CalculateROIWidget';
import ContactUsWidget from '../widgets/interactive/ContactUsWidget';

// Dynamically import with SSR disabled
const LearnMoreShowcase = dynamic(() => import('../widgets/showcases/LearnMoreShowcase'), { ssr: false });

type PricingPageStaticProps = {
  scrollingLogos: StrapiApiScrollingLogo[];
  articles: StrapiApiArticle[];
};

export const getStaticProps: GetStaticProps<PricingPageStaticProps> = async (context) => {
  const scrollingLogos = await StrapiApiClient.getAllScrollingLogos();
  const articles = await StrapiApiClient.getRandomMostRecentArticles();
  const props: PricingPageStaticProps = { scrollingLogos, articles };
  return withGlobalDefaultStaticProps(context, { props });
};

const PricingPage: NextPageWithLayout<PricingPageStaticProps> = (props) => {
  const { scrollingLogos, articles } = props;
  const seoConfig = getPageSeoConfig(pageRoutes.Pricing);
  return (
    <>
      <NextSeo {...seoConfig} />
      <HeaderSection
        type={HeaderSectionType.WhitePlain}
        title="Unlimited usage at no extra cost"
        description="Here is a small sample of our pricing. No hidden fees, usage limits or premium features, only full on capabilities and the best solutions for your company!"
      >
        <PricingCards mode={'light'} />
      </HeaderSection>
      <PageSection type={PageSectionType.PurpleDiagonal}>
        <CalculateROIWidget />
      </PageSection>
      <PageSection isNarrow={true}>
        <ScrollingLogosWidget logos={scrollingLogos} />
      </PageSection>
      <PageSection isNarrowTop={true}>
        <ContactUsWidget defaultMode={ContactUsMode.FreeTrial} showAllModes={true} />
      </PageSection>
      <PageSection>
        <LearnMoreShowcase articles={articles} />
      </PageSection>
    </>
  );
};
PricingPage.displayName = 'PricingPage';
export default PricingPage;
